
import { useRef, useEffect, useState } from 'react';
import { NodeResizer } from '@xyflow/react';

interface EquipmentNodeProps {
  id: string;
  data: any;
  selected: boolean;
}

const EquipmentNode = ({ id, data, selected }: EquipmentNodeProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [textValue, setTextValue] = useState(data.text || "Typ hier...");
  const [isEditing, setIsEditing] = useState(false);

  // Add a highlight effect for selected nodes
  useEffect(() => {
    if (nodeRef.current) {
      if (selected) {
        nodeRef.current.classList.add('node-selected');
      } else {
        nodeRef.current.classList.remove('node-selected');
      }
    }
  }, [selected]);

  // Auto-adjust the textarea height
  useEffect(() => {
    if (textareaRef.current && data.id === 'tekst' && isEditing) {
      const adjustHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
          // Reset height to auto to get the correct scrollHeight
          textarea.style.height = 'auto';
          // Set to scrollHeight to fit content
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      };
      
      adjustHeight();
    }
  }, [textValue, data.id, isEditing]);

  // Exit editing mode when clicking outside
  useEffect(() => {
    if (!selected && isEditing) {
      setIsEditing(false);
    }
  }, [selected, isEditing]);

  // Calculate appropriate font size based on node dimensions
  const calculateFontSize = () => {
    if (!nodeRef.current) return '16px';
    
    const width = nodeRef.current.getBoundingClientRect().width;
    const height = nodeRef.current.getBoundingClientRect().height;
    
    // Scale font based on the smaller dimension to prevent overflow
    const baseFontSize = Math.min(width / 10, height / 4);
    
    // Set reasonable limits
    return `${Math.max(12, Math.min(baseFontSize, 32))}px`;
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (data.id === 'tekst') {
      e.stopPropagation();
      setIsEditing(true);
      
      // Focus the textarea after a small delay to ensure it's rendered
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          
          // Clear default text on first edit if it's still the default
          if (textValue === "Typ hier...") {
            setTextValue("");
            data.text = "";
          }
        }
      }, 10);
    }
  };

  if (data.id === 'tekst') {
    return (
      <div 
        ref={nodeRef}
        style={{ 
          width: '100%', 
          height: '100%', 
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `rotate(${data.rotation || 0}deg) scaleX(${data.flipX ? -1 : 1}) scaleY(${data.flipY ? -1 : 1})`,
          zIndex: data.zIndex || 'auto',
          outline: selected ? '2px solid #ff0071' : 'none',
          transitionProperty: 'outline',
          transitionDuration: '0.1s',
          overflow: 'hidden'
        }}
        onDoubleClick={handleDoubleClick}
      >
        {selected && (
          <NodeResizer 
            minWidth={100} 
            minHeight={30}
            isVisible={selected}
            lineStyle={{ border: '2px solid #ff0071' }}
            handleStyle={{ 
              width: 8, 
              height: 8, 
              background: '#ffffff',
              border: '2px solid #ff0071'
            }}
          />
        )}
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={textValue}
            className="w-full h-full resize-none border-none shadow-none focus-visible:ring-0 bg-transparent text-center"
            style={{ 
              fontSize: calculateFontSize(),
              lineHeight: '1.2',
              padding: '4px',
              cursor: 'text',
              overflow: 'hidden'
            }}
            onClick={e => e.stopPropagation()}
            onChange={e => {
              setTextValue(e.target.value);
              data.text = e.target.value;
            }}
            onBlur={() => setIsEditing(false)}
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center text-center px-2"
            style={{ 
              fontSize: calculateFontSize(),
              lineHeight: '1.2',
              userSelect: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word'
            }}
          >
            {textValue}
          </div>
        )}
      </div>
    );
  }

  // Calculate font size for icon nodes
  const calculateIconFontSize = () => {
    if (!nodeRef.current) return '24px';
    
    const width = nodeRef.current.getBoundingClientRect().width;
    const height = nodeRef.current.getBoundingClientRect().height;
    
    // Use the smaller dimension to ensure the icon fits
    const size = Math.min(width, height);
    return `${Math.max(16, size * 0.6)}px`;
  };

  return (
    <div 
      ref={nodeRef}
      style={{ 
        width: '100%', 
        height: '100%', 
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `rotate(${data.rotation || 0}deg) scaleX(${data.flipX ? -1 : 1}) scaleY(${data.flipY ? -1 : 1})`,
        zIndex: data.zIndex || 'auto',
        minWidth: '50px', 
        minHeight: '50px',
        outline: selected ? '2px solid #ff0071' : 'none',
        transitionProperty: 'outline',
        transitionDuration: '0.1s'
      }}
      className={selected ? 'node-selected' : ''}
    >
      <div style={{ 
        position: 'absolute',
        inset: '5%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {selected && (
          <NodeResizer 
            minWidth={30} 
            minHeight={30}
            isVisible={selected}
            lineStyle={{ border: '2px solid #ff0071' }}
            handleStyle={{ 
              width: 8, 
              height: 8, 
              background: '#ffffff',
              border: '2px solid #ff0071'
            }}
          />
        )}
        {data.isImage ? (
          <img 
            src={data.image || data.icon} 
            alt={data.name || "Afbeelding"}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              pointerEvents: 'none'
            }}
            onError={(e) => {
              console.error("Fout bij laden afbeelding:", e);
              // Visuele feedback als de afbeelding niet kan worden geladen
              const target = e.target as HTMLImageElement;
              target.style.border = '2px dashed red';
              target.style.padding = '5px';
            }}
          />
        ) : (
          <div style={{ 
            pointerEvents: 'none',
            fontSize: calculateIconFontSize(),
            lineHeight: 1,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: data.color,
            ...(data.filled === false && {
              WebkitTextStroke: `2px ${data.color}`,
              WebkitTextFillColor: 'transparent'
            })
          }}>
            {data.icon}
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentNode;
